import { useState, useEffect, useCallback, useRef } from "react";

const useApi = (apiFunction, executeImmediately = true) => {
  const isApiCallingRef = useRef(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const callApi = useCallback(
    async (params = null, callback) => {
      // cancel any previous ongoing request
      // if (abortControllerRef.current) {
      //   abortControllerRef.current.abort();
      // }
      // 🚫 BLOCK MULTIPLE CLICKS
      if (isApiCallingRef.current) {
        // console.warn("API already in progress");
        return;
      }

      isApiCallingRef.current = true;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        const response = await apiFunction(params, {
          signal: controller.signal,
        });

        // Ignore response if request was aborted
        if (controller.signal.aborted) return;

        setData(response.data);
        setError(null);
        if (callback) callback(response.data, null);
      } catch (err) {
        // Ignore abort errors, they are expected
        if (err?.code === "ERR_CANCELED") return;

        const errorMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Something went wrong!";
        if (callback) callback(null, errorMessage, err?.status);
        setError(err);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
        // 🔓 RELEASE LOCK
        isApiCallingRef.current = false;
      }
    },
    [apiFunction],
  );

  useEffect(() => {
    if (executeImmediately) {
      callApi();
    }

    return () => {
      cancelRequest();
    };
  }, [executeImmediately, callApi]);

  const cancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  return { data, error, loading, callApi, cancelRequest };
};

export default useApi;
